import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { resolveTipCheckoutSubmitError } from '../features/tips-checkout/model/tip-checkout-submit-error';
import {
  clampTipCheckoutAmount,
  isTipCheckoutPayableAmount,
  TIP_CHECKOUT_DEFAULT_AMOUNT,
  TIP_CHECKOUT_PROVIDER_FEE_RATE,
} from '../features/tips-checkout/model/tips-checkout.constants';
import { TipCheckoutPayerContactSheet } from '../features/tips-checkout/ui/tip-checkout-payer-contact-sheet';
import {
  TipCheckoutScreen,
  type TipCheckoutPaymentDraft,
} from '../features/tips-checkout/ui/tip-checkout-screen';
import {
  getTipperEmployeeProfile,
  thankTipper,
  type ThankArgs,
} from '../shared/api/tipper.service';
import { parseAmount } from '../shared/model';
import { ViewportFrame, ViewportPage } from '../shared/ui';

import { persistTipThanksRewardAmount } from './tip-thanks-page.state';

const TIP_PAGE_STATE_QUERY_PARAM = 'state';
const TIP_PAGE_PROMO_CARD_STATE = 'promo-card';
const TIP_PAGE_GIFT_CLAIM_STATE = 'gift-claim';
const TIP_PAGE_AMOUNT_QUERY_PARAM = 'amount';

function resolveTipPageSelectedAmount(searchParams: URLSearchParams) {
  const parsedAmount = parseAmount(
    searchParams.get(TIP_PAGE_AMOUNT_QUERY_PARAM) ?? '',
  );
  const normalizedAmount = clampTipCheckoutAmount(parsedAmount);

  return normalizedAmount ?? TIP_CHECKOUT_DEFAULT_AMOUNT;
}

function createTipperRecipientProfileQueryKey(walletUUID?: string) {
  return ['tipper', 'employee-profile', walletUUID] as const;
}

function createIdempotencyKey() {
  return crypto.randomUUID();
}

function buildThankArgs(
  paymentAttempt: TipCheckoutPaymentDraft,
  walletUUID: string,
  phoneNumber?: string,
): ThankArgs {
  const thankArgs: ThankArgs = {
    amount: paymentAttempt.amount,
    comment: paymentAttempt.comment,
    debugTopUpNow: true,
    feeBearer: paymentAttempt.isFeeIncluded ? 'sender' : 'receiver',
    idempotencyKey: createIdempotencyKey(),
    walletId: walletUUID,
  };

  if (paymentAttempt.score !== null) {
    thankArgs.score = paymentAttempt.score;
  }

  if (phoneNumber) {
    thankArgs.phoneNumber = phoneNumber;
  }

  return thankArgs;
}

function resolveRecipientRewardAmount(
  amount: number,
  feeBearer: ThankArgs['feeBearer'],
) {
  if (feeBearer === 'sender') {
    return amount;
  }

  return Math.max(
    0,
    amount - Math.ceil(amount * TIP_CHECKOUT_PROVIDER_FEE_RATE),
  );
}

export function TipPage() {
  const { t } = useTranslation();
  const { walletUUID } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPayerContactOpen, setPayerContactOpen] = useState(false);
  const [paymentDraft, setPaymentDraft] =
    useState<TipCheckoutPaymentDraft | null>(null);
  const { data: recipientProfile } = useQuery({
    enabled: typeof walletUUID === 'string' && walletUUID.length > 0,
    queryFn: () => getTipperEmployeeProfile(walletUUID as string),
    queryKey: createTipperRecipientProfileQueryKey(walletUUID),
  });
  const thankMutation = useMutation({
    mutationFn: thankTipper,
    onSuccess: (result, thankArgs) => {
      if (!walletUUID) {
        return;
      }

      const rewardAmount = resolveRecipientRewardAmount(
        thankArgs.amount,
        thankArgs.feeBearer,
      );

      persistTipThanksRewardAmount(walletUUID, rewardAmount);
      setPaymentDraft(null);
      setPayerContactOpen(false);
      globalThis.location.assign(result.paymentUrl);
    },
  });
  const pageState = searchParams.get(TIP_PAGE_STATE_QUERY_PARAM);
  const selectedTipAmount = resolveTipPageSelectedAmount(searchParams);
  const isLegacyThanksState = pageState === TIP_PAGE_GIFT_CLAIM_STATE;
  const shouldShowPromoCard = pageState === TIP_PAGE_PROMO_CARD_STATE;
  const paymentError = resolveTipCheckoutSubmitError(thankMutation.error, t);

  if (!walletUUID) {
    return <Navigate replace to="/home" />;
  }

  const syncSelectedTipAmount = (
    nextSelectedTipAmount: number,
    shouldForceSync = false,
  ) => {
    const normalizedAmount =
      clampTipCheckoutAmount(nextSelectedTipAmount) ??
      TIP_CHECKOUT_DEFAULT_AMOUNT;

    if (!shouldForceSync && normalizedAmount === selectedTipAmount) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set(TIP_PAGE_AMOUNT_QUERY_PARAM, String(normalizedAmount));
    setSearchParams(nextSearchParams, { replace: true });
  };

  const openPayerContactSheet = (nextPaymentDraft: TipCheckoutPaymentDraft) => {
    if (
      !walletUUID ||
      thankMutation.isPending ||
      !isTipCheckoutPayableAmount(nextPaymentDraft.amount)
    ) {
      return;
    }

    syncSelectedTipAmount(nextPaymentDraft.amount, true);
    thankMutation.reset();
    setPaymentDraft(nextPaymentDraft);
    setPayerContactOpen(true);
  };

  const closePayerContactSheet = () => {
    if (thankMutation.isPending) {
      return;
    }

    thankMutation.reset();
    setPaymentDraft(null);
    setPayerContactOpen(false);
  };

  const submitPayment = async (phoneNumber?: string) => {
    if (
      !walletUUID ||
      paymentDraft === null ||
      thankMutation.isPending ||
      !isTipCheckoutPayableAmount(paymentDraft.amount)
    ) {
      return;
    }

    try {
      await thankMutation.mutateAsync(
        buildThankArgs(paymentDraft, walletUUID, phoneNumber),
      );
    } catch {
      return;
    }
  };

  const updateSelectedTipAmount = (nextSelectedTipAmount: number) => {
    syncSelectedTipAmount(nextSelectedTipAmount);
  };

  if (isLegacyThanksState) {
    return <Navigate replace to={`/tip/${walletUUID}/thanks`} />;
  }

  return (
    <ViewportPage dataUi="tip-page">
      <ViewportFrame
        dataUi="tip-page-frame"
        paddingBottomPx={28}
        paddingLeftPx={30}
        paddingRightPx={30}
        paddingTopPx={20}
      >
        <TipCheckoutScreen
          initialAmount={selectedTipAmount}
          isPaying={thankMutation.isPending}
          onAmountChange={updateSelectedTipAmount}
          onPay={openPayerContactSheet}
          paymentError={isPayerContactOpen ? null : paymentError}
          recipientFirstName={recipientProfile?.firstName}
          recipientProfile={recipientProfile}
          showPromoCard={shouldShowPromoCard}
        />
        <TipCheckoutPayerContactSheet
          isOpen={isPayerContactOpen}
          isSubmitting={thankMutation.isPending}
          onClose={closePayerContactSheet}
          onContinue={submitPayment}
          submitError={paymentError}
        />
      </ViewportFrame>
    </ViewportPage>
  );
}
